import React, { useState, useEffect } from 'react';
import { 
    Box, Paper, Typography, Button, IconButton, TableContainer, Table, TableHead, TableRow, TableBody, TableCell, Chip, Skeleton, TablePagination, Tooltip
} from '@mui/material';
import LockClockIcon from '@mui/icons-material/LockClock';
import HistoryIcon from '@mui/icons-material/History';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import ReplayIcon from '@mui/icons-material/Replay';
import { FinanceService } from '../../../api/services/financeService';
import NightAuditModal from './NightAuditModal';
import { exportClientSide, convertCsvToExportFormat } from '../../../utils/exportUtils';
import ExportFormatModal from '../../../components/ExportFormatModal';
import { toast } from '../../../utils/toast';
import { formatCurrency } from '../../../utils/currency';
import { extractTotalCount, standardSecondaryButtonSx } from '../../../components/common/tableStyles';
import { parseLocalDate, formatDateDisplay } from '../../../utils/dateUtils';

export default function FinanceNightAuditTab() {
    // History State
    const [auditHistory, setAuditHistory] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [auditModalOpen, setAuditModalOpen] = useState(false);
    const [isDownloadingReport, setIsDownloadingReport] = useState(false);
    const [exportModalOpen, setExportModalOpen] = useState(false);

    // Pagination
    const [page, setPage] = useState(0);
    const [limit, setLimit] = useState(20);
    const [totalRecords, setTotalRecords] = useState(0);

    useEffect(() => {
        fetchAuditHistory();
    }, [page, limit]);

    useEffect(() => {
        const handleAuditCompleted = () => {
            fetchAuditHistory();
        };
        window.addEventListener('nightAuditCompleted', handleAuditCompleted);
        return () => {
            window.removeEventListener('nightAuditCompleted', handleAuditCompleted);
        };
    }, [page, limit]);
    const fetchAuditHistory = async () => {
        setHistoryLoading(true);
        try {
            const data = await FinanceService.getNightAuditHistory({ page, limit });
            const dataArray = Array.isArray(data.data) ? data.data : (Array.isArray(data) ? data : []);
            setAuditHistory(dataArray);
            setTotalRecords(extractTotalCount(data, dataArray));
        } catch (error) {
            console.error('Failed to load audit history:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setLimit(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDownloadReport = async (format) => {
        setExportModalOpen(false);
        setIsDownloadingReport(true);
        try {
            const date = new Date().toISOString().split('T')[0];
            const res = await FinanceService.generateNightAuditReport({ format: 'csv', date });
            
            let csvText = '';
            if (res.data instanceof Blob) {
                csvText = await res.data.text();
            } else if (typeof res.data === 'string') {
                csvText = res.data;
            } else {
                csvText = JSON.stringify(res.data || res);
            }

            const fileNameBase = `night_audit_revenue_report_${date}`;
            await convertCsvToExportFormat(csvText, format, fileNameBase);

            toast.success('Report downloaded successfully');
        } catch (error) {
            console.error('Download report error', error);
            toast.error('Failed to generate report');
        } finally {
            setIsDownloadingReport(false);
        }
    };

    const renderDate = (date) => formatDateDisplay(date);
    const renderTime = (date) => date ? new Date(date).toLocaleTimeString() : '';

    const today = parseLocalDate(new Date());
    const hasRunToday = auditHistory.some(log => {
        if (!log.date && !log.createdAt) return false;
        const logDate = parseLocalDate(log.date || log.createdAt);
        if (!logDate) return false;
        const isToday = (
            logDate.getDate() === today.getDate() &&
            logDate.getMonth() === today.getMonth() &&
            logDate.getFullYear() === today.getFullYear()
        );
        return isToday && (log.status === 'Completed' || log.status === 'Balanced' || log.status === 'Success');
    });

    return (
        <Box>
            <Paper sx={{ 
                p: { xs: 4, md: 6 }, 
                borderRadius: 4, 
                border: 'none', 
                mb: 4, 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)',
                boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                color: 'white',
                position: 'relative',
                overflow: 'hidden'
            }}>
                <Box sx={{
                    position: 'absolute',
                    top: '-50%',
                    left: '-20%',
                    width: '600px',
                    height: '600px',
                    background: 'radial-gradient(circle, rgba(99,102,241,0.15) 0%, rgba(0,0,0,0) 70%)',
                    pointerEvents: 'none'
                }} />
                <Box sx={{ 
                    p: 2, 
                    borderRadius: '50%', 
                    background: 'rgba(255,255,255,0.1)', 
                    backdropFilter: 'blur(10px)',
                    mb: 3,
                    border: '1px solid rgba(255,255,255,0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: '0 0 30px rgba(236,72,153,0.3)'
                }}>
                    <LockClockIcon sx={{ fontSize: 50, color: '#f472b6' }} />
                </Box>
                <Typography variant="h4" fontWeight="800" sx={{ mb: 1.5, letterSpacing: '-0.5px' }}>
                    Daily Night Audit Routine
                </Typography>
                <Typography variant="body1" sx={{ mb: 5, maxWidth: 600, textAlign: 'center', color: 'rgba(255,255,255,0.7)', fontSize: '1.1rem', lineHeight: 1.6 }}>
                    Execute the daily night audit routine to validate pending arrivals, unsettled folios, and roll the operational business date forward.
                </Typography>
                
                {historyLoading ? (
                    <Skeleton variant="rectangular" width={280} height={48} sx={{ borderRadius: 2 }} />
                ) : !hasRunToday ? (
                    <Button 
                        variant="contained" 
                        color="error" 
                        size="large"
                        onClick={() => setAuditModalOpen(true)}
                        sx={{ 
                            px: 5, 
                            py: 1.8, 
                            borderRadius: 8, 
                            background: 'linear-gradient(45deg, #ec4899 30%, #f43f5e 90%)',
                            boxShadow: '0 4px 20px rgba(236, 72, 153, 0.4)',
                            fontWeight: 'bold',
                            fontSize: '1.05rem',
                            textTransform: 'none',
                            transition: 'all 0.3s',
                            '&:hover': {
                                background: 'linear-gradient(45deg, #db2777 30%, #e11d48 90%)',
                                transform: 'translateY(-2px)',
                                boxShadow: '0 6px 25px rgba(236, 72, 153, 0.6)'
                            }
                        }}
                    >
                        Launch Night Audit Dashboard
                    </Button>
                ) : (
                    <Chip 
                        icon={<LockClockIcon style={{ color: '#4ade80' }}/>} 
                        label="Night Audit Completed for Today" 
                        sx={{ 
                            px: 3, 
                            py: 3, 
                            borderRadius: 4, 
                            fontWeight: 'bold', 
                            fontSize: '1.1rem',
                            background: 'rgba(22, 163, 74, 0.15)',
                            color: '#4ade80',
                            border: '1px solid rgba(74, 222, 128, 0.3)',
                            backdropFilter: 'blur(8px)',
                            boxShadow: '0 4px 20px rgba(22, 163, 74, 0.2)'
                        }} 
                    />
                )}
            </Paper>

            <Box sx={{ mt: 5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <HistoryIcon color="primary" sx={{ mr: 1, fontSize: 28 }} />
                        <Typography variant="h6" fontWeight="bold">
                            Night Audit History Logs
                        </Typography>
                    </Box>
                    <Button 
                        variant="outlined" 
                        startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 18 }} />}
                        onClick={() => setExportModalOpen(true)}
                        disabled={isDownloadingReport}
                        sx={standardSecondaryButtonSx}
                    >
                        {isDownloadingReport ? 'Generating...' : 'Download Today\'s Report'}
                    </Button>
                    <ExportFormatModal
                        open={exportModalOpen}
                        onClose={() => setExportModalOpen(false)}
                        onExport={handleDownloadReport}
                        isExporting={isDownloadingReport}
                        title="Export Today's Audit Report"
                    />
                </Box>
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Audit Run ID</TableCell>
                                <TableCell>Execution Datetime</TableCell>
                                <TableCell>Closed Business Date</TableCell>
                                <TableCell>Metrics Summary</TableCell>
                                <TableCell>System State</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {historyLoading ? (
                                Array.from(new Array(3)).map((_, idx) => (
                                    <TableRow key={idx}>
                                        <TableCell><Skeleton variant="text" width="60%" /></TableCell>
                                        <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                                        <TableCell><Skeleton variant="text" width="80%" /></TableCell>
                                        <TableCell><Skeleton variant="text" width="90%" /></TableCell>
                                        <TableCell><Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} /></TableCell>
                                    </TableRow>
                                ))
                            ) : auditHistory.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                                        <Typography color="text.secondary">No historical night audit logs found.</Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                auditHistory.map((log) => {
                                    const isBalanced = log.status === 'Completed' || log.status === 'Balanced' || log.status === 'Success';
                                    const metrics = log.metrics || {};
                                    const hasCompletedRunForDate = auditHistory.some(h => 
                                        h.date && log.date && 
                                        new Date(h.date).getTime() === new Date(log.date).getTime() && 
                                        ['Completed', 'Balanced', 'Success'].includes(h.status)
                                    );
                                    
                                    return (
                                        <TableRow key={log.id} hover>
                                            <TableCell>
                                                <Typography variant="body2" fontFamily="monospace" fontWeight="bold">
                                                    {log.id.split('-')[0].toUpperCase()}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {renderDate(log.createdAt || log.date)} {renderTime(log.createdAt || log.date)}
                                            </TableCell>
                                            <TableCell>
                                                <b>{renderDate(log.date)}</b>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                                                    {(() => {
                                                        const metricData = [];
                                                        if (metrics.closingBalance !== undefined) {
                                                            metricData.push({ id: 'bal', label: `Balance: ${formatCurrency(metrics.closingBalance)}`, color: 'default' });
                                                        }
                                                        if (metrics.openFoliosCount !== undefined) {
                                                            metricData.push({ id: 'fol', label: `Open Folios: ${metrics.openFoliosCount}`, color: 'default' });
                                                        }
                                                        if (metrics.noShowsProcessed !== undefined && metrics.noShowsProcessed > 0) {
                                                            metricData.push({ id: 'nos', label: `No-Shows: ${metrics.noShowsProcessed}`, color: 'warning' });
                                                        }
                                                        if (metrics.postedRoomCharges !== undefined && metrics.postedRoomCharges > 0) {
                                                            metricData.push({ id: 'chg', label: `Room Charges: ${metrics.postedRoomCharges}`, color: 'success' });
                                                        }
                                                        
                                                        if (metricData.length === 0) return <Typography variant="caption" color="text.secondary">-</Typography>;
                                                        
                                                        const visible = metricData.slice(0, 2);
                                                        const hidden = metricData.slice(2);
                                                        
                                                        return (
                                                            <>
                                                                {visible.map(item => (
                                                                    <Chip key={item.id} size="small" variant="outlined" color={item.color} label={item.label} />
                                                                ))}
                                                                
                                                                {hidden.length > 0 && (
                                                                    <Tooltip 
                                                                        placement="top"
                                                                        title={
                                                                            <Box sx={{ p: 0.5 }}>
                                                                                {hidden.map(item => (
                                                                                    <Typography key={item.id} variant="caption" display="block" color="inherit" sx={{ fontWeight: 'bold' }}>
                                                                                        • {item.label}
                                                                                    </Typography>
                                                                                ))}
                                                                            </Box>
                                                                        }
                                                                    >
                                                                        <Chip size="small" label={`+${hidden.length}`} sx={{ cursor: 'pointer', bgcolor: 'rgba(0,0,0,0.06)' }} />
                                                                    </Tooltip>
                                                                )}
                                                            </>
                                                        );
                                                    })()}
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Chip 
                                                        label={log.status || 'Completed'} 
                                                        color={isBalanced ? 'success' : 'error'} 
                                                        size="small" 
                                                        sx={{ fontWeight: 'bold' }}
                                                    />
                                                    {log.status === 'Failed' && !hasCompletedRunForDate && (
                                                        <Tooltip title="Rerun Audit">
                                                            <IconButton 
                                                                size="small" 
                                                                color="error" 
                                                                onClick={() => setAuditModalOpen(true)}
                                                                sx={{ 
                                                                    border: '1px solid rgba(211, 47, 47, 0.5)', 
                                                                    bgcolor: 'rgba(211, 47, 47, 0.04)',
                                                                    '&:hover': { bgcolor: 'rgba(211, 47, 47, 0.12)' }
                                                                }}
                                                            >
                                                                <ReplayIcon fontSize="small" />
                                                            </IconButton>
                                                        </Tooltip>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                    <TablePagination
                        component="div"
                        count={auditHistory.length === 0 ? 0 : totalRecords}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={limit}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[20, 50, 100]}
                    />
                </TableContainer>
            </Box>

            <NightAuditModal
                open={auditModalOpen}
                onClose={() => setAuditModalOpen(false)}
                onSuccess={() => {
                    fetchAuditHistory();
                    window.dispatchEvent(new Event('nightAuditCompleted'));
                }}
            />
        </Box>
    );
}
